export function getImageUrl(formats, preferredSize = "medium", fallbackUrl) {
  if (!formats || typeof formats !== "object") {
    return fallbackUrl || null;
  }

  // First try the preferred size
  if (formats[preferredSize]?.url) {
    return formats[preferredSize].url;
  }

  // If preferred size not available, try large
  if (formats.large?.url) {
    return formats.large.url;
  }

  // If large not available, try small
  if (formats.small?.url) {
    return formats.small.url;
  }

  // If none of the above work, try any available size
  const availableSizes = ["large", "medium", "small"];
  for (const size of availableSizes) {
    if (formats[size]?.url) {
      return formats[size].url;
    }
  }

  return fallbackUrl || null;
}

export function getImageSrcSet(formats) {
  if (!formats || typeof formats !== "object") {
    return undefined;
  }

  const srcsetParts = [];

  if (formats.thumbnail?.url && formats.thumbnail?.width) {
    srcsetParts.push(`${formats.thumbnail.url} ${formats.thumbnail.width}w`);
  }
  if (formats.small?.url && formats.small?.width) {
    srcsetParts.push(`${formats.small.url} ${formats.small.width}w`);
  }
  if (formats.medium?.url && formats.medium?.width) {
    srcsetParts.push(`${formats.medium.url} ${formats.medium.width}w`);
  }
  if (formats.large?.url && formats.large?.width) {
    srcsetParts.push(`${formats.large.url} ${formats.large.width}w`);
  }

  return srcsetParts.length > 0 ? srcsetParts.join(", ") : undefined;
}

export function getImageDimensions(image, preferredSize = "medium") {
  if (!image) return { width: undefined, height: undefined };

  const formats = image?.formats || {};
  const preferred = formats[preferredSize];

  if (preferred?.width && preferred?.height) {
    return { width: preferred.width, height: preferred.height };
  }

  return {
    width: image.width,
    height: image.height,
  };
}

export function getResponsiveImageProps(image, options = {}) {
  const {
    preferredSize = "medium",
    alt = "",
    loading = "lazy",
    priority = false,
    sizes = "100vw",
  } = options;

  if (!image) {
    return { alt };
  }

  const formats = image?.formats || {};
  const src = getImageUrl(formats, preferredSize) || image?.url;
  const srcSet = getImageSrcSet(formats);
  const { width, height } = getImageDimensions(image, preferredSize);

  return {
    src,
    srcSet,
    sizes,
    alt,
    width,
    height,
    loading: priority ? "eager" : loading,
    fetchPriority: priority ? "high" : undefined,
    decoding: priority ? "sync" : "async",
  };
}
